/**
 * RelationshipPermissionsSection Component
 * Unified table for follower + friend permissions with side-by-side toggles.
 * Follower column is tier-gated (Pro+), friend column is open to all.
 */

import { TierBadge } from '@components/ui/TierBadge';
import UpgradeModal from '@components/ui/UpgradeModal';
import { useAuth } from '@contexts/AuthContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useUserProfile } from '@hooks/useUserProfile';
import {
  DEFAULT_FOLLOWER_PERMISSIONS,
  DEFAULT_FRIEND_PERMISSIONS,
  type FollowerPermissionKey,
  type FollowerPermissions,
  type FriendPermissionKey,
  isPermissionAllowed,
  PERMISSION_CONFIG,
} from '@parlay-picker/shared/tierPermissions';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Component
// =============================================================================

export function RelationshipPermissionsSection(): ReactElement {
  const { user, authFetch } = useAuth();
  const { tier } = useFeatureAccess();
  const { profile } = useUserProfile({ userId: user?.id ?? '' });
  const ownProfile = profile && !profile.limited ? profile : null;

  const [followerPerms, setFollowerPerms] = useState<FollowerPermissions>({
    ...DEFAULT_FOLLOWER_PERMISSIONS,
  });
  const [friendPerms, setFriendPerms] = useState<Record<FriendPermissionKey, boolean>>({
    ...DEFAULT_FRIEND_PERMISSIONS,
  });
  const [saving, setSaving] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Initialize follower permissions from profile
  useEffect(() => {
    const followerPermissions = ownProfile?.user.followerPermissions;
    if (followerPermissions) {
      setFollowerPerms({
        showDetailedAnalysis: followerPermissions.showDetailedAnalysis ?? false,
        allowTailParlays: followerPermissions.allowTailParlays ?? false,
        showPropBreakdown: followerPermissions.showPropBreakdown ?? false,
        showRecentBets: followerPermissions.showRecentBets ?? false,
        showActivityFeed: followerPermissions.showActivityFeed ?? false,
        showFinancialDetails: followerPermissions.showFinancialDetails ?? false,
      });
    }
  }, [ownProfile?.user.followerPermissions]);

  // Initialize friend permissions from profile
  useEffect(() => {
    const friendPermissions = ownProfile?.user.friendPermissions;
    if (friendPermissions) {
      setFriendPerms({
        showDetailedAnalysis: friendPermissions.showDetailedAnalysis ?? true,
        allowTailParlays: friendPermissions.allowTailParlays ?? true,
        showPropBreakdown: friendPermissions.showPropBreakdown ?? true,
        showRecentBets: friendPermissions.showRecentBets ?? true,
        showActivityFeed: friendPermissions.showActivityFeed ?? true,
        showFinancialDetails: friendPermissions.showFinancialDetails ?? false,
      });
    }
  }, [ownProfile?.user.friendPermissions]);

  const handleSaveFollowerPermissions = useCallback(
    async (permissions: FollowerPermissions): Promise<void> => {
      const response = await authFetch('/api/auth/follower-permissions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ followerPermissions: permissions }),
      });
      if (!response.ok) {
        throw new Error('Failed to update permissions');
      }
    },
    [authFetch],
  );

  const handleSaveFriendPermissions = useCallback(
    async (permissions: Record<FriendPermissionKey, boolean>): Promise<void> => {
      const response = await authFetch('/api/auth/friend-permissions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ friendPermissions: permissions }),
      });
      if (!response.ok) {
        throw new Error('Failed to update friend permissions');
      }
    },
    [authFetch],
  );

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      await Promise.all([
        handleSaveFollowerPermissions(followerPerms),
        handleSaveFriendPermissions(friendPerms),
      ]);
    } catch {
      // Errors silently handled — toast system can be added later
    }
    setSaving(false);
  };

  const isLocked = (permission: FollowerPermissionKey): boolean => {
    return !isPermissionAllowed(tier, permission);
  };

  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Relationship Permissions</h3>
      <p className="text-sm text-theme-text-tertiary mb-3">
        Control what followers and friends can access on your profile
      </p>
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
        {/* Column headers */}
        <div className="grid grid-cols-[1fr_72px_72px] gap-2 mb-2 pb-2 border-b border-theme-border">
          <div />
          <div className="text-center text-[10px] font-semibold uppercase tracking-wider text-theme-text-tertiary">
            Followers
          </div>
          <div className="text-center text-[10px] font-semibold uppercase tracking-wider text-theme-text-tertiary">
            Friends
          </div>
        </div>

        {/* Permission rows */}
        <div className="space-y-0.5">
          {PERMISSION_CONFIG.map((perm) => {
            const followerLocked = isLocked(perm.key);
            const followerDepDisabled = perm.dependsOnRecentBets && !followerPerms.showRecentBets;
            const friendDepDisabled = perm.dependsOnRecentBets && !friendPerms.showRecentBets;

            return (
              <div
                key={perm.key}
                className="grid grid-cols-[1fr_72px_72px] gap-2 py-2.5 border-b border-theme-border/30 last:border-b-0"
              >
                {/* Label */}
                <div>
                  <div className="text-sm font-medium text-theme-text flex items-center gap-2">
                    {perm.label}
                  </div>
                  <div className="text-xs text-theme-text-tertiary">{perm.description}</div>
                </div>

                {/* Follower checkbox */}
                <div className="flex items-center justify-center">
                  {followerLocked ? (
                    <button
                      type="button"
                      onClick={() => setShowUpgradeModal(true)}
                      className="flex items-center gap-1"
                    >
                      <TierBadge feature="social_follow" size="sm" />
                    </button>
                  ) : (
                    <input
                      type="checkbox"
                      checked={followerPerms[perm.key]}
                      onChange={(e) =>
                        setFollowerPerms({ ...followerPerms, [perm.key]: e.target.checked })
                      }
                      disabled={followerDepDisabled}
                      className="w-4 h-4 text-theme-primary bg-theme-bg border-theme-border rounded focus:ring-theme-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                  )}
                </div>

                {/* Friend checkbox */}
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={friendPerms[perm.key]}
                    onChange={(e) =>
                      setFriendPerms({ ...friendPerms, [perm.key]: e.target.checked })
                    }
                    disabled={friendDepDisabled}
                    className="w-4 h-4 text-theme-primary bg-theme-bg border-theme-border rounded focus:ring-theme-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Save button */}
        <div className="pt-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>

      {showUpgradeModal && (
        <UpgradeModal
          mode="feature"
          feature="social_follow"
          onDismiss={() => setShowUpgradeModal(false)}
        />
      )}
    </section>
  );
}
